import { ReactNode } from "react";
import { Outlet, RouteObject } from "react-router-dom";
import { LoginView } from "../components/LoginView";
import { LogoutView } from "../components/LogoutView";
import { OidcGuard } from "../components/OidcGuard";
import { OidcProvider } from "../components/OidcProvider";
import { OidcAuthenticating } from "../components/OidcStateComponents";
import { SessionLostView } from "../components/SessionLostView";

export interface CreateRoutesProps {
  errorElement?: ReactNode | null;
  loginLogoElement?: ReactNode | null;
  hrefDataProtectionGeneral?: string | undefined;
  hrefDataProtectionEmployees?: string | undefined;
  hrefDataProtectionBusinessPartners?: string | undefined;
  hrefCookiePolicy?: string | undefined;
  hrefImprint?: string | undefined;

  oidcPaths?: Partial<OidcPaths>;
  unguardedRoutes?: RouteObject[];
  guardedRoutes?: RouteObject[];
}

export interface OidcPaths {
  authorize: string;
  login: string;
  logout: string;
  sessionLost: string;
}

const defaultOidcPaths: OidcPaths = {
  authorize: "authorize",
  login: "login",
  logout: "logout",
  sessionLost: "session-lost",
};

export const createRoutes = (
  {
    errorElement,
    loginLogoElement,
    hrefDataProtectionGeneral,
    hrefDataProtectionEmployees,
    hrefDataProtectionBusinessPartners,
    hrefImprint,
    hrefCookiePolicy,
    unguardedRoutes,
    guardedRoutes,
    oidcPaths,
  }: CreateRoutesProps,
): RouteObject[] => {
  const paths: OidcPaths = {
    ...defaultOidcPaths,
    ...oidcPaths,
  };

  return [
    {
      element: (
        <OidcProvider oidcPaths={paths}>
          <Outlet/>
        </OidcProvider>
      ),
      errorElement,
      children: [
        {
          children: [
            {
              path: paths.authorize,
              element: <OidcAuthenticating/>,
            },
            {
              path: paths.login,
              element: (
                <LoginView
                  logoElement={loginLogoElement}
                  hrefDataProtectionGeneral={hrefDataProtectionGeneral}
                  hrefDataProtectionEmployees={hrefDataProtectionEmployees}
                  hrefDataProtectionBusinessPartners={hrefDataProtectionBusinessPartners}
                  hrefCookiePolicy={hrefCookiePolicy}
                  hrefImprint={hrefImprint}
                />
              ),
            },
            {
              path: paths.logout,
              element: <LogoutView oidcPaths={paths}/>,
            },
            {
              path: paths.sessionLost,
              element: <SessionLostView oidcPaths={paths}/>,
            },
          ],
        },
        {
          children: unguardedRoutes ?? [],
          errorElement,
        },
        {
          element: <OidcGuard oidcPaths={paths}><Outlet/></OidcGuard>,
          children: guardedRoutes ?? [],
          errorElement,
        },
      ],
    },
  ];
};
